import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Map as GoogleMap, AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { Earth, Home, MapPinned } from 'lucide-react';
import { MapExpertAvatarPin } from './maps';
import { APP_CONFIG } from '../config/app';
import { getExpertAvatarUrl, resolveMapMarkerIconUrl } from '../utils/imageUtils';
import { anonymizeLastName } from '../utils/anonymizeNames';
import { fetchJson } from '../utils/fetchJson';

type Freelancer = {
  _id: string;
  firstName: string;
  lastName: string;
  role?: string;
  profileImage?: string;
  city?: string;
  postalCode?: string;
};

type LatLng = { lat: number; lng: number };

const CoverageContactSection: React.FC = () => {
  const googleMapsMapId = APP_CONFIG.googleMapsMapId;

  const OVERLAP_OFFSETS: Array<Pick<LatLng, 'lat' | 'lng'>> = useMemo(
    () => [
      { lat: 0, lng: 0 },
      { lat: 0.00018, lng: 0 },
      { lat: -0.00018, lng: 0 },
      { lat: 0, lng: 0.00018 },
      { lat: 0, lng: -0.00018 },
      { lat: 0.00018, lng: 0.00018 },
      { lat: -0.00018, lng: -0.00018 },
      { lat: 0.00018, lng: -0.00018 },
      { lat: -0.00018, lng: 0.00018 },
    ],
    []
  );

  const defaultCenter = useMemo<LatLng>(
    () => ({
      lat: 46.603354, // Centre de la France
      lng: 1.888334,
    }),
    []
  );

  // Track whether the Maps API is ready (window.google available)
  const [mapsReady, setMapsReady] = useState(false);

  const [freelancers, setFreelancers] = useState<Freelancer[]>([]);
  const [markers, setMarkers] = useState<Array<{ freelancer: Freelancer; position: LatLng }>>([]);
  const [mapCenter, setMapCenter] = useState<LatLng>(defaultCenter);
  const [selected, setSelected] = useState<{ freelancer: Freelancer; position: LatLng } | null>(null);

  const displayMarkers = useMemo(() => {
    // Si plusieurs freelancers partagent exactement les mêmes coordonnées,
    // ils peuvent être superposés visuellement => on décale légèrement.
    const posToItems = new Map<string, Array<{ idx: number; item: { freelancer: Freelancer; position: LatLng } }>>();

    markers.forEach((item, idx) => {
      const key = `${item.position.lat.toFixed(5)},${item.position.lng.toFixed(5)}`;
      const prev = posToItems.get(key) || [];
      prev.push({ idx, item });
      posToItems.set(key, prev);
    });

    const result: Array<{ freelancer: Freelancer; position: LatLng }> = [];

    markers.forEach((item, originalIdx) => {
      const key = `${item.position.lat.toFixed(5)},${item.position.lng.toFixed(5)}`;
      const group = posToItems.get(key) || [];
      const inGroupIdx = group.findIndex(g => g.idx === originalIdx);

      if (group.length <= 1 || inGroupIdx < 0) {
        result.push(item);
        return;
      }

      const offset = OVERLAP_OFFSETS[inGroupIdx % OVERLAP_OFFSETS.length];
      result.push({
        freelancer: item.freelancer,
        position: {
          lat: item.position.lat + offset.lat,
          lng: item.position.lng + offset.lng,
        },
      });
    });

    return result;
  }, [markers, OVERLAP_OFFSETS]);

  // Poll until window.google is available (loaded by APIProvider)
  useEffect(() => {
    if ((window as { google?: unknown }).google) {
      setMapsReady(true);
      return;
    }
    const id = setInterval(() => {
      if ((window as { google?: unknown }).google) {
        setMapsReady(true);
        clearInterval(id);
      }
    }, 100);
    return () => clearInterval(id);
  }, []);

  // Récupère les freelancers (endpoint public via /api/users)
  useEffect(() => {
    let cancelled = false;

    const fetchFreelancers = async () => {
      try {
        const data = await fetchJson<{ users?: Freelancer[] }>(
          '/api/users?role=fondateur,freelancer,freelancer_admin'
        );
        if (cancelled) return;
        setFreelancers(data.users || []);
      } catch (err) {
        console.error('[CoverageContactSection]', err);
        if (!cancelled) setFreelancers([]);
      }
    };

    fetchFreelancers();

    return () => {
      cancelled = true;
    };
  }, []);

  // Géocode côté frontend => le modèle User ne stocke pas de lat/lng
  useEffect(() => {
    if (!mapsReady) return;

    const googleObj = (window as { google?: { maps?: { Geocoder?: new () => google.maps.Geocoder } } })?.google;
    const GeocoderCtor = googleObj?.maps?.Geocoder;
    if (!GeocoderCtor) return;
    if (freelancers.length === 0) return;

    let cancelled = false;
    const geocoder = new GeocoderCtor();

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const geocodeAddress = (
      address: string
    ): Promise<{ coords: LatLng | null; status: string }> => {
      return new Promise(resolve => {
        geocoder.geocode({ address }, (results, status) => {
          if (status === 'OK' && results?.[0]?.geometry?.location) {
            const loc = results[0].geometry.location;
            resolve({ coords: { lat: loc.lat(), lng: loc.lng() }, status });
          } else {
            resolve({ coords: null, status });
          }
        });
      });
    };

    const geocodeWithRetry = async (
      address: string,
      retries: number
    ): Promise<{ coords: LatLng | null; status: string }> => {
      let last = { coords: null as LatLng | null, status: 'UNKNOWN_ERROR' };
      for (let attempt = 0; attempt <= retries; attempt++) {
        // Petit délai progressif en cas de soucis temporaires
        if (attempt > 0) await delay(350 * attempt);
        const res = await geocodeAddress(address);
        last = res;
        if (res.coords) return res;

        // Statuts qui peuvent être temporaires => on retente
        if (res.status === 'OVER_QUERY_LIMIT' || res.status === 'UNKNOWN_ERROR') {
          continue;
        }

        // Pour ZERO_RESULTS / INVALID_REQUEST, inutile de retenter
        break;
      }
      return last;
    };

    const keyFor = (f: Freelancer) => `${(f.postalCode || '').trim()}|${(f.city || '').trim()}`;
    const toAddress = (postalCode?: string, city?: string) => {
      const pc = (postalCode || '').trim();
      const c = (city || '').trim();
      if (pc && c) return `${pc} ${c} France`;
      if (c) return `${c} France`;
      return '';
    };

    const run = async () => {
      try {
        const uniqueKeys = Array.from(new Set(freelancers.map(keyFor))).filter(k => {
          const [postalCode, city] = k.split('|');
          return Boolean(postalCode) || Boolean(city);
        });

        const coordsByKey = new Map<string, LatLng>();
        // Géocoder séquentiellement pour limiter les risques de quota/burst.
        for (const k of uniqueKeys) {
          if (cancelled) return;
          const [postalCode, city] = k.split('|');
          const address1 = toAddress(postalCode, city);
          if (!address1) continue;

          // 1) Priorité : code postal + ville + France
          let { coords } = await geocodeWithRetry(address1, 2);

          // 2) Fallback : seulement ville + France (si le code postal est inexistant/ambigu)
          const cityOnly = city ? `${city} France` : '';
          if (!coords && cityOnly && cityOnly !== address1) {
            const fallback = await geocodeWithRetry(cityOnly, 2);
            coords = fallback.coords;
          }

          if (coords) coordsByKey.set(k, coords);
          await delay(250);
        }

        if (cancelled) return;

        const enriched = freelancers
          .map(f => {
            const k = keyFor(f);
            const position = coordsByKey.get(k);
            if (!position) return null;
            return { freelancer: f, position };
          })
          .filter(Boolean) as Array<{ freelancer: Freelancer; position: LatLng }>;

        if (enriched.length > 0) setMapCenter(enriched[0].position);
        setMarkers(enriched);
      } catch (err) {
        console.error('[CoverageContactSection][geocode] ', err);
        if (!cancelled) setMarkers([]);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [freelancers, mapsReady]);

  return (
    <section className="relative bg-[#2F3136] section-spacing overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-80px] left-[10%] w-64 h-64 bg-[#5865F2]/20 rounded-full blur-3xl" />
        <div className="absolute bottom-[-100px] right-[8%] w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center section-title-spacing"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <div className="section-subtitle-badge gap-2">
            <MapPinned size={16} />
            <span className="text-sm font-medium">Zones couvertes</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Assistance rapide, proche de <span className="text-[#5865F2]">vous</span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Intervention a distance dans toute la France et deplacement a domicile autour de Geneve
            (Ain et Haute-Savoie).
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6">
          <motion.div
            className="rounded-2xl border border-white/10 bg-[#36393F]/80 p-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold">Carte simplifiee de couverture</h3>
              <span className="text-xs text-gray-400">Mise a jour en continu</span>
            </div>

            <div className="relative rounded-xl border border-white/10 bg-[#2B2D31] min-h-[320px] overflow-hidden">
              {APP_CONFIG.googleMapsApiKey ? (
                <GoogleMap
                  style={{ width: '100%', height: '320px' }}
                  defaultCenter={mapCenter}
                  center={mapCenter}
                  defaultZoom={6}
                  mapTypeControl={false}
                  streetViewControl={false}
                  fullscreenControl={false}
                  clickableIcons={false}
                  mapId={googleMapsMapId || undefined}
                >
                  {displayMarkers.map(m => {
                    const imgUrl = resolveMapMarkerIconUrl(getExpertAvatarUrl(m.freelancer.profileImage));
                    return (
                      <AdvancedMarker
                        key={m.freelancer._id}
                        position={m.position}
                        title={`${m.freelancer.firstName} ${m.freelancer.lastName}`}
                        onClick={() => setSelected(m)}
                      >
                        <MapExpertAvatarPin
                          src={imgUrl}
                          alt={`${m.freelancer.firstName} ${m.freelancer.lastName}`}
                        />
                      </AdvancedMarker>
                    );
                  })}
                  {selected && (
                    <InfoWindow
                      position={selected.position}
                      onCloseClick={() => setSelected(null)}
                    >
                      <div className="p-2 min-w-[180px]">
                        <div className="flex items-center space-x-2">
                          <img
                            src={resolveMapMarkerIconUrl(getExpertAvatarUrl(selected.freelancer.profileImage))}
                            alt={`${selected.freelancer.firstName} ${anonymizeLastName(selected.freelancer.lastName)}`}
                            className="w-10 h-10 rounded-full"
                          />
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {selected.freelancer.firstName} {anonymizeLastName(selected.freelancer.lastName)}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {[selected.freelancer.postalCode, selected.freelancer.city]
                                .filter(Boolean)
                                .join(' ')}
                            </p>
                          </div>
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              ) : (
                <iframe
                  title="Zone d'intervention SOS IT Services"
                  src="https://www.google.com/maps?q=Geneve%20France&z=9&output=embed"
                  className="absolute inset-0 w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              )}
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <motion.div
                className="rounded-lg border border-[#5865F2]/40 bg-[#5865F2]/15 p-4"
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-md bg-[#5865F2]/25 text-[#C8D3FF]">
                    <Earth className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-[#B7C4FF] text-xs uppercase tracking-wide mb-1">À distance</p>
                    <p className="text-white font-semibold">Partout en France</p>
                    <p className="text-gray-300 text-sm mt-1">Où que vous soyez en France, un expert peut vous assister à distance.</p>
                  </div>
                </div>
              </motion.div>
              <motion.div
                className="rounded-lg border border-cyan-400/30 bg-cyan-400/10 p-4"
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: 0.1, ease: 'easeOut' }}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-md bg-cyan-400/20 text-cyan-100">
                    <Home className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-cyan-200 text-xs uppercase tracking-wide mb-1">A domicile</p>
                    <p className="text-white font-semibold">Geneve + 50 km</p>
                    <p className="text-gray-300 text-sm mt-1">Ain, Haute-Savoie et alentours.</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CoverageContactSection;
